import {
  type FC,
  type PropsWithChildren,
  type ReactNode,
  useEffect,
  useRef,
} from "react";
import { useI18nContext } from "@hopper-b2b/i18n";
import { useSetBodyModalOpenClass } from "@hopper-b2b/utilities";
import { Box, Dialog, type DialogProps, Slide } from "@material-ui/core";
import clsx from "clsx";

import { ButtonWrap } from "../ButtonWrap";
import { IconName } from "../Icon";
import { IconComponent } from "../IconComponent";
import "./styles.scss";

export type DesktopPopupModalDirection = "up" | "down" | "left" | "right";

export interface IDesktopPopupModalProps {
  open: boolean;
  className?: string;
  contentClassName?: string;
  closeButtonClassName?: string;
  direction?: DesktopPopupModalDirection;
  headerElement?: JSX.Element | string;
  hideXButton?: boolean;
  maxWidth?: DialogProps["maxWidth"];
  bottomButton?: JSX.Element;
  onClose?: () => void;
  invisibleBackdrop?: boolean;
  children?: ReactNode;
  closeIcon?: JSX.Element;
}

export const DesktopPopupModal: FC<IDesktopPopupModalProps> = ({
  bottomButton,
  children,
  className,
  contentClassName,
  closeButtonClassName,
  direction,
  headerElement,
  hideXButton = false,
  invisibleBackdrop = true,
  maxWidth,
  onClose,
  open,
  closeIcon,
}: PropsWithChildren<IDesktopPopupModalProps>) => {
  const { t } = useI18nContext();
  useSetBodyModalOpenClass(open);

  const titleId = `${Math.trunc(Math.random() * 10 ** 10)}DialogTitle`;

  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (open)
      setTimeout(() => {
        closeRef.current?.focus();
      }, 10);
  }, [open]);

  return (
    <Dialog
      hideBackdrop={false}
      BackdropProps={{
        invisible: invisibleBackdrop,
      }}
      maxWidth={maxWidth}
      open={open}
      onClose={onClose}
      TransitionComponent={Slide}
      TransitionProps={{
        // See https://mui.com/material-ui/transitions/#performance-amp-seo
        unmountOnExit: true,

        // @ts-ignore: Fix the type definition of TransitionProps to include direction.
        direction: direction ?? "up",
      }}
      className={clsx("desktop-popover-card-root", className)}
      onClick={(event) => {
        event.stopPropagation();
      }}
      role="dialog"
      aria-labelledby={titleId}
    >
      {headerElement && (
        <div id={titleId} tabIndex={-1} className="desktop-popover-card-header">
          {headerElement}
        </div>
      )}
      {!hideXButton && (
        <Box
          className={clsx(
            "desktop-popover-card-top-button",
            "right",
            closeButtonClassName
          )}
        >
          <ButtonWrap aria-label="Close" onClick={onClose} ref={closeRef}>
            {closeIcon ?? (
              <IconComponent
                ariaLabel={t("close.imageAlt")}
                className={clsx("close-button-icon")}
                name={IconName.Close}
              />
            )}
          </ButtonWrap>
        </Box>
      )}
      <Box
        className={clsx(
          "desktop-popover-card-content-container",
          contentClassName
        )}
        role={!headerElement ? "dialog" : undefined}
        tabIndex={!headerElement ? -1 : undefined}
        id={!headerElement ? titleId : undefined}
      >
        {children}
      </Box>
      <Box className="desktop-popover-card-bottom-button">{bottomButton}</Box>
    </Dialog>
  );
};
